<template>
    <div class="business">
        <!-- 页面标题 -->
        <section class="page-header">
            <div class="container">
                <h1 class="page-title">业务范围</h1>
                <div class="breadcrumb">
                    <el-breadcrumb separator="/">
                        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
                        <el-breadcrumb-item>业务范围</el-breadcrumb-item>
                    </el-breadcrumb>
                </div>
            </div>
        </section>

        <!-- 业务列表 -->
        <section class="business-list">
            <div class="container">
                <div class="business-items">
                    <div class="business-item" v-for="(item, index) in businessList" :key="index"
                        :class="{ 'reverse': index % 2 !== 0 }">
                        <div class="business-image" data-aos="fade-right" data-aos-duration="1000">
                            <el-image :src="item.image" fit="cover">
                                <template #placeholder>
                                    <div class="image-placeholder">
                                        <el-icon>
                                            <Picture />
                                        </el-icon>
                                    </div>
                                </template>
                            </el-image>
                        </div>
                        <div class="business-content" data-aos="fade-left" data-aos-duration="1000">
                            <div class="content-inner">
                                <div class="business-icon">
                                    <el-icon :size="40">
                                        <component :is="item.icon" />
                                    </el-icon>
                                </div>
                                <h3>{{ item.title }}</h3>
                                <div class="divider"></div>
                                <p class="description">{{ item.description }}</p>
                                <ul class="features">
                                    <li v-for="feature in item.features" :key="feature">
                                        <el-icon>
                                            <Check />
                                        </el-icon>
                                        <span>{{ feature }}</span>
                                    </li>
                                </ul>
                                <el-button type="primary" @click="handleContact">了解更多</el-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 合作流程 -->
        <section class="process">
            <div class="container">
                <h2 class="section-title text-center">合作流程</h2>
                <div class="divider center"></div>
                <div class="process-steps">
                    <div class="step" v-for="(step, index) in processSteps" :key="index" data-aos="zoom-in"
                        data-aos-duration="800" :data-aos-delay="index * 100">
                        <div class="step-icon">
                            <el-icon :size="36">
                                <component :is="step.icon" />
                            </el-icon>
                        </div>
                        <div class="step-content">
                            <h4>{{ step.title }}</h4>
                            <p>{{ step.description }}</p>
                        </div>
                        <div class="step-number">{{ index + 1 }}</div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AOS from 'aos'
import 'aos/dist/aos.css'
import {
    Picture,
    Check,
    ChatLineRound,
    Document,
    SetUp,
    Finished
} from '@element-plus/icons-vue'

const router = useRouter()

// 业务列表数据
const businessList = [
    {
        title: '软件开发',
        description: '提供专业的软件定制开发服务，包括企业管理系统、移动应用、微信小程序等，助力企业数字化转型。',
        image: '/images/business/software-dev.jpg',
        icon: 'Monitor',
        features: [
            '企业管理系统开发',
            '移动应用开发',
            '微信小程序开发',
            '系统集成服务'
        ]
    },
    {
        title: '网络推广',
        description: '提供全方位的网络营销解决方案，帮助企业提升品牌知名度，获取精准客户资源。',
        image: '/images/business/marketing.jpg',
        icon: 'Promotion',
        features: [
            'SEO优化服务',
            '社交媒体运营',
            '内容营销策划',
            '数据分析优化'
        ]
    },
    {
        title: '智能应用',
        description: '专注于AI技术的商业化应用，为企业提供智能化解决方案，提升运营效率。',
        image: '/images/business/ai-tech.jpg',
        icon: 'Connection',
        features: [
            '机器学习应用',
            '计算机视觉',
            '自然语言处理',
            '智能决策系统'
        ]
    },
    {
        title: '社交媒体',
        description: '开发运营自有社交媒体平台，为用户提供优质的社交体验和内容服务。',
        image: '/images/business/social-media.jpg',
        icon: 'DataAnalysis',
        features: [
            '社交平台开发',
            '用户运营服务',
            '内容生态建设',
            '社群运营管理'
        ]
    }
]

// 合作流程数据
const processSteps = [
    {
        title: '需求沟通',
        description: '深入了解客户需求，提供专业咨询',
        icon: ChatLineRound
    },
    {
        title: '方案制定',
        description: '定制个性化解决方案',
        icon: Document
    },
    {
        title: '项目实施',
        description: '专业团队开发实施',
        icon: SetUp
    },
    {
        title: '交付使用',
        description: '上线部署及售后服务',
        icon: Finished
    }
]

// 跳转到联系我们页面
const handleContact = () => {
    router.push('/contact')
}

// 初始化动画库
onMounted(() => {
    AOS.init({
        duration: 1000,
        once: true
    })
})
</script>

<style scoped lang="scss">
.business {
    padding-top: 80px;

    .page-header {
        background: linear-gradient(135deg, #1890ff 0%, #604FDF 100%);
        padding: 60px 0;
        color: #fff;
        margin-bottom: 60px;

        .page-title {
            font-size: 36px;
            margin-bottom: 10px;
        }

        .breadcrumb {
            :deep(.el-breadcrumb) {
                color: rgba(255, 255, 255, 0.8);

                .el-breadcrumb__item {
                    .el-breadcrumb__inner {
                        color: rgba(255, 255, 255, 0.8);

                        &:hover {
                            color: #fff;
                        }
                    }

                    &:last-child .el-breadcrumb__inner {
                        color: #fff;
                    }
                }
            }
        }
    }

    .section-title {
        font-size: 32px;
        color: #333;
        margin-bottom: 20px;
    }

    .divider {
        width: 60px;
        height: 3px;
        background: var(--el-color-primary);
        margin-bottom: 40px;

        &.center {
            margin-left: auto;
            margin-right: auto;
        }
    }

    // 业务列表样式
    .business-list {
        padding: 60px 0;

        .business-items {
            .business-item {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 60px;
                margin-bottom: 100px;
                align-items: center;

                &:last-child {
                    margin-bottom: 0;
                }

                &.reverse {
                    direction: rtl;

                    .business-content {
                        direction: ltr;
                    }
                }

                .business-image {
                    .el-image {
                        width: 100%;
                        height: 400px;
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
                    }

                    .image-placeholder {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        background: #f5f5f5;
                        color: #999;
                    }
                }

                .business-content {
                    .content-inner {
                        max-width: 480px;
                        margin: 0 auto;

                        .business-icon {
                            color: var(--el-color-primary);
                            margin-bottom: 20px;
                        }

                        h3 {
                            font-size: 32px;
                            color: #333;
                            margin-bottom: 20px;
                        }

                        .description {
                            font-size: 16px;
                            line-height: 1.8;
                            color: #666;
                            margin-bottom: 30px;
                        }

                        .features {
                            list-style: none;
                            padding: 0;
                            margin-bottom: 30px;

                            li {
                                display: flex;
                                align-items: center;
                                margin-bottom: 15px;
                                font-size: 16px;
                                color: #333;

                                .el-icon {
                                    color: var(--el-color-success);
                                    margin-right: 10px;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // 合作流程样式
    .process {
        padding: 80px 0;
        background: #f8f9fa;

        .process-steps {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 30px;
            margin-top: 60px;

            .step {
                text-align: center;
                position: relative;

                &:not(:last-child)::after {
                    content: '';
                    position: absolute;
                    top: 40px;
                    right: -15px;
                    width: 30px;
                    height: 2px;
                    background: #ddd;
                }

                .step-icon {
                    width: 80px;
                    height: 80px;
                    margin: 0 auto 20px;
                    background: #fff;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    color: var(--el-color-primary);
                    position: relative;
                    z-index: 1;
                }

                .step-content {
                    h4 {
                        font-size: 20px;
                        color: #333;
                        margin-bottom: 10px;
                    }

                    p {
                        font-size: 14px;
                        color: #666;
                        line-height: 1.6;
                    }
                }

                .step-number {
                    position: absolute;
                    top: -10px;
                    right: -10px;
                    width: 30px;
                    height: 30px;
                    background: var(--el-color-primary);
                    color: #fff;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 16px;
                }
            }
        }
    }
}

// 响应式设计
@media (max-width: 992px) {
    .business {
        .business-list {
            .business-items {
                .business-item {
                    grid-template-columns: 1fr;
                    gap: 40px;
                    margin-bottom: 60px;

                    &.reverse {
                        direction: ltr;
                    }

                    .business-image {
                        .el-image {
                            height: 300px;
                        }
                    }

                    .business-content {
                        .content-inner {
                            max-width: 100%;
                        }
                    }
                }
            }
        }

        .process {
            .process-steps {
                grid-template-columns: repeat(2, 1fr);
                gap: 40px;

                .step {
                    &:nth-child(2)::after {
                        display: none;
                    }

                    &:nth-child(odd)::after {
                        display: none;
                    }
                }
            }
        }
    }
}

@media (max-width: 768px) {
    .business {
        .page-header {
            padding: 40px 0;

            .page-title {
                font-size: 28px;
            }
        }

        .process {
            .process-steps {
                grid-template-columns: 1fr;

                .step::after {
                    display: none;
                }
            }
        }
    }
}
</style>
